import { toast } from 'svelte-sonner';
import { SvelteDate, SvelteSet } from 'svelte/reactivity';
import { weekendsCoveredBy } from './lib';
import type { RelatedJobMap, saveSchedulerRequestBody, SchedularVehicles, SiteVisitMap, VehicleAssignmentMap, WeekendOverride } from './types';

export const toReqBody: (vehicleAssignments: VehicleAssignmentMap, siteVisits?: SiteVisitMap) => saveSchedulerRequestBody = (
    vehicleAssignments,
    siteVisits,
) => {
    const toReturnJobs: saveSchedulerRequestBody['vehicleAssignments'] = [];
    for (const [_, vehicleAssignment] of vehicleAssignments) {
        if (!vehicleAssignment.modified) continue;
        // Skip unscheduled items unless they're being deleted
        if (vehicleAssignment.start === null && !vehicleAssignment.deleted) continue;

        // Virtual rows ('site-visits' top row, or 'unassigned') save as null vehicle_id
        const vehicleId =
            vehicleAssignment.vehicle?.id === 'site-visits' || vehicleAssignment.vehicle?.id === 'unassigned' ? null : vehicleAssignment.vehicle?.id;

        // Check if this is a new assignment (id === job.id means no VehicleAssignment exists yet)
        const isNewAssignment = vehicleAssignment.id === vehicleAssignment.job?.id;

        // Drop any weekend_overrides whose weekend the VA no longer covers
        // (e.g. left over from a move or resize). Keeps the JSON in sync with
        // the VA's current range and avoids stale entries piling up over time.
        const liveOverrides: WeekendOverride[] = (() => {
            const all = vehicleAssignment.weekend_overrides ?? [];
            if (all.length === 0 || !vehicleAssignment.start) return [];
            const covered = new SvelteSet(weekendsCoveredBy(new SvelteDate(vehicleAssignment.start), vehicleAssignment.duration));
            return all.filter((o) => covered.has(o.weekend_start));
        })();

        toReturnJobs.push({
            // For new assignments, don't send id (let backend create new one), but send temp_id for mapping
            id: isNewAssignment ? '' : vehicleAssignment.id,
            temp_id: isNewAssignment ? vehicleAssignment.id : undefined,
            start_date_time: vehicleAssignment.start ? vehicleAssignment.start.toISOString() : '',
            end_date_time: vehicleAssignment.end ? vehicleAssignment.end.toISOString() : '',
            vehicle_id: vehicleId,
            duplicated: vehicleAssignment.duplicated,
            culmex_job_id: vehicleAssignment.job.id,
            // Carry phase identity through to the backend so the singleton
            // constraint and primary-window guard scope correctly per phase.
            job_phase_id: (vehicleAssignment as any).job_phase_id ?? null,
            employee_assignments: vehicleAssignment.employeeAssignments,
            job_type: vehicleAssignment.job_type,
            title: vehicleAssignment.title,
            weekend_overrides: liveOverrides,
            deleted: vehicleAssignment.deleted,
        });
    }

    const toReturnSiteVisits: saveSchedulerRequestBody['siteVisits'] = [];
    if (siteVisits) {
        for (const [_, siteVisit] of siteVisits) {
            if (!siteVisit.modified) continue;
            // Skip unscheduled items unless they're being deleted
            if (!siteVisit.start && !siteVisit.deleted) continue;

            // Virtual rows ('site-visits' top row, or 'unassigned') save as null vehicle_id
            const vehicleId = siteVisit.vehicle?.id === 'site-visits' || siteVisit.vehicle?.id === 'unassigned' ? null : siteVisit.vehicle?.id;

            toReturnSiteVisits.push({
                id: siteVisit.id,
                visitable_id: siteVisit.visitable_id,
                visitable_type: siteVisit.visitable_type,
                title: siteVisit.title,
                purpose: siteVisit.purpose,
                status: siteVisit.status,
                start_date_time: siteVisit.start ? siteVisit.start.toISOString() : '',
                end_date_time: siteVisit.end ? siteVisit.end.toISOString() : '',
                notes: siteVisit.notes,
                findings: siteVisit.findings,
                employee_assignments: siteVisit.employeeAssignments,
                vehicle_id: vehicleId,
            });
        }
    }
    //console.log("toReqBody", {toReturnJobs, toReturnSiteVisits});

    return {
        vehicleAssignments: toReturnJobs,
        siteVisits: toReturnSiteVisits,
    };
};

export const saveSvelte = async ({
    loading,
    baseUrl,
    csrfToken,
    vehicleAssignments,
    vehicles,
    relatedJobsMap,
    siteVisits,
}: {
    loading: string[];
    baseUrl: string;
    csrfToken: string;
    vehicleAssignments: VehicleAssignmentMap;
    vehicles: SchedularVehicles;
    relatedJobsMap: RelatedJobMap;
    siteVisits?: SiteVisitMap;
}) => {
    const loadingIndex = loading.push('save-scheduler');
    // console.log({l({oading, loadingIndex});
    (async () => {
        const requestBody = toReqBody(vehicleAssignments, siteVisits);
        const url = new URL(`/web-api/schedule`, baseUrl);

        const res = await fetch(url, {
            method: 'POST',
            body: JSON.stringify(requestBody),
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json, text-plain, */*',
                'X-Requested-With': 'XMLHttpRequest',
                'X-CSRF-TOKEN': csrfToken,
            },
            credentials: 'same-origin',
        });
        const json = await res.json();

        // if (res.status !== 200 && res.status !== 201) {
        //     throw new Error(`Error from api: ${res.status}, ${res.statusText}. ${json?.message}`);
        // }

        // for (const job of jobs.values()) {
        //     if (job.duplicated) {
        //         jobs.delete(job.uuid);
        //     }
        //     if (job.original_job_id !== null && job.start === null) {
        //         jobs.delete(job.uuid);
        //     }
        //     job.modified = false;
        // }

        // const updatedJobs = json.updatedJobs;
        // for (const updatedJob of updatedJobs) {
        //     if (updatedJob.duplicated === false || updatedJob.duplicated === null) {
        //         continue;
        //     }
        //     const newJob = $state(loadJob({
        //         ...updatedJob,
        //         duplicated: false,
        //     }, vehicles));
        //     vehicleAssignments.set(newJob.id, newJob);
        // }
        // buildRelatedJobsMap(vehicleAssignments, relatedJobsMap);
        // console.log("saved jobs", {vehicleAssignments});

        if (json.errors && json.errors.length > 0) {
            toast.warning(`Saved with warnings: ${json.errors.join(', ')}`);
        } else {
            toast.success('Successfully saved');
        }
    })()
        .catch((e) => {
            toast.error(`Failed to save: ${e.message}`);
            console.error(e);
        })
        .finally(() => {
            loading.splice(loadingIndex - 1);
        });
};
